<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :style="{ width: '450px' }"
    :header="t('campaignsAutomationList.actions.delete')"
  >
    <div class="p-fluid">
      <p>{{ t('campaignsAutomationList.actions.deleteConfirm') }}</p>
      <p
        v-if="currentCampaign"
        class="font-semibold mt-2"
      >
        {{ currentCampaign.name }}
      </p>
    </div>
    <template #footer>
      <SpmButton
        :label="t('no')"
        icon="pi pi-times"
        class-style="p-button p-button-secondary"
        @click="close"
      />
      <SpmButton
        :label="t('yes')"
        icon="pi pi-check"
        class-style="p-button p-button-danger"
        :disabled="isLoading"
        @click="confirm"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { showToastError, showToastSuccess } from '@/helpers';
import { CampaignAutomationStatusEnum } from '@/types';
import { setRefreshScenariosList } from '@/composables/AutomatedScenarios';
import { updateAutomationStatus } from '@/composables/flows/useFlowApi';
import type { AutomationCampaignData, CampaignActionPayload, DeleteCampaignResult } from './types';

export default defineComponent({
  name: 'DeleteCampaignAction',

  components: {
    Dialog,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();

    // Internal state
    const isVisible = ref(false);
    const isLoading = ref(false);
    const currentCampaign = ref<AutomationCampaignData | null>(null);

    /**
     * Open the dialog with the given payload
     */
    const open = (payload: CampaignActionPayload) => {
      currentCampaign.value = payload.campaign;
      isVisible.value = true;
    };

    /**
     * Close the dialog
     */
    const close = () => {
      isVisible.value = false;
      currentCampaign.value = null;
    };

    /**
     * Confirm the action and emit result
     */
    const confirm = async () => {
      if (!currentCampaign.value) return;

      isLoading.value = true;

      try {
        await updateAutomationStatus(currentCampaign.value.id, CampaignAutomationStatusEnum.DELETED);

        showToastSuccess(t('campaignsAutomationList.actions.deleteSuccess'));
        setRefreshScenariosList(true);

        const result: DeleteCampaignResult = {
          campaign: currentCampaign.value,
          success: true,
        };

        emit('confirm', result);
        close();
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : t('errorMessages.GENERIC_ERROR');
        showToastError(errorMessage);
      } finally {
        isLoading.value = false;
      }
    };

    // Expose open method for parent component
    expose({ open });

    return {
      t,
      isVisible,
      isLoading,
      currentCampaign,
      close,
      confirm,
    };
  },
});
</script>
